document.addEventListener("alpine:init", () => {
  Alpine.data(
    "bundles",
    ({
      otpFirst,
      badgePattern,
      pricePattern,
      currencyDivider,
      displayCompareAtPrice,
      displaySubscriptionBadge,
      displayOTPBadge,
    }) => {
      return {
        products: [],
        flavors: [],
        currentFlavor: null,
        bundleForm: null,

        displayCompareAtPrice: displayCompareAtPrice == "true",
        displaySubscriptionBadge: displaySubscriptionBadge == "true",
        displayOTPBadge: displayOTPBadge == "true",

        // On homepage we want the badge to be a full string like "Save 38%"
        // Default pattern supports injecting the number and will append "%" if missing.
        badgePattern: (badgePattern ?? "Save [percentage]").toString(),
        pricePattern: pricePattern ?? "[amount]",
        currencyDivider: currencyDivider ?? 100,

        purchaseTypes: null,
        purchaseType: null,
        isSubscription: true,
        otpFirst: otpFirst || false,

        init() {
          this.bundleForm = this.$refs?.bundleForm || null;

          // Homepage markup may not have purchase types; keep this optional.
          this.products = this.$refs?.productsData
            ? JSON.parse(this.$refs.productsData.textContent)
            : [];

          this.purchaseTypes = this.$refs?.purchaseTypes
            ? JSON.parse(this.$refs.purchaseTypes.textContent)
            : [{ id: "subscription" }, { id: "otp" }];

          this.prepareFlavorsData();

          // Default selection
          const wanted = this.otpFirst ? "otp" : "subscription";
          const hasWanted = this.purchaseTypes?.some((t) => t?.id === wanted);
          this.changeSelectedPurchaseType(hasWanted ? wanted : (this.purchaseTypes?.[0]?.id || "subscription"));
        },

        buildBadgeText(pct) {
          const safePct = Number.isFinite(pct) ? Math.max(0, Math.round(pct)) : 0;
          let pattern = (this.badgePattern ?? "Save [percentage]").toString().trim();

          // If pattern includes placeholder, swap it. Otherwise treat as a prefix.
          let text = pattern.includes("[percentage]")
            ? pattern.replace("[percentage]", String(safePct))
            : `${pattern} ${safePct}`.trim();

          // Ensure there's a percent sign somewhere in the final text.
          if (!/%/.test(text)) text = `${text}%`;

          return text;
        },

        formatBenefits(benefits) {
          if (!benefits) return "";

          return `<ul class="ctr-bundle__item-list">
            ${benefits
              .split("\n")
              .map((benefit) => {
                return `<li>${benefit
                  .replace(
                    "[icon-delivery]",
                    "<span class='ctr-icon m-delivery'></span>"
                  )
                  .replace(
                    "[icon-time]",
                    "<span class='ctr-icon m-time'></span>"
                  )}</li>`;
              })
              .join("")}
          </ul>`;
        },

        prepareFlavorsData() {
          if (!this.products || !Array.isArray(this.products)) return;

          this.flavors = [];

          this.products.forEach(
            ({
              product,
              metafields,
              custom_bundle_image: customBundleImage,
              custom_bundle_title: customBundleTitle,
              promotional_price: promotionalPrice,
              subscription_promotional_price: subscriptionPromotionalPrice,
              subscription_promo_label: subscriptionPromoLabel,
            }) => {
              if (!product || !product.variants || !product.variants.length) return;

              const firstVariant = product.variants[0];
              const alternativeImage = customBundleImage || null;

              const flavorImage =
                alternativeImage ||
                firstVariant.featured_image ||
                product.featured_image;

              const variantPrice = Number(firstVariant.price || 0);
              const productCompareAt = Number(product.compare_at_price || 0);
              const variantCompareAt = Number(firstVariant.compare_at_price || 0);

              // Use the regular one-time "reference" price for savings math:
              // prefer compare_at_price (if higher than current), else fallback to current price.
              const oneTimeReferencePrice =
                (productCompareAt && productCompareAt > variantPrice)
                  ? productCompareAt
                  : (variantCompareAt && variantCompareAt > variantPrice)
                    ? variantCompareAt
                    : variantPrice;

              const hasSellingPlan = !!(firstVariant.selling_plan_allocations && firstVariant.selling_plan_allocations.length);
              const subscriptionPrice = hasSellingPlan
                ? Number(firstVariant.selling_plan_allocations[0].price || 0)
                : variantPrice;

              const subscriptionOldPrice = oneTimeReferencePrice;
              const parsedPromotionalPrice = Number(promotionalPrice);
              const parsedSubscriptionPromotionalPrice = Number(
                subscriptionPromotionalPrice
              );
              const promotionalPriceValue =
                Number.isFinite(parsedPromotionalPrice) &&
                parsedPromotionalPrice > 0
                  ? parsedPromotionalPrice
                  : null;
              const subscriptionPromotionalPriceValue =
                Number.isFinite(parsedSubscriptionPromotionalPrice) &&
                parsedSubscriptionPromotionalPrice > 0
                  ? parsedSubscriptionPromotionalPrice
                  : null;
              const effectiveOtpPrice =
                promotionalPriceValue || variantPrice;
              const effectiveSubscriptionPrice =
                subscriptionPromotionalPriceValue || subscriptionPrice;

              const subscriptionPct =
                oneTimeReferencePrice > 0
                  ? ((oneTimeReferencePrice - effectiveSubscriptionPrice) / oneTimeReferencePrice) * 100
                  : 0;

              const otpPct =
                oneTimeReferencePrice > 0
                  ? ((oneTimeReferencePrice - effectiveOtpPrice) / oneTimeReferencePrice) * 100
                  : 0;

              this.flavors.push({
                parentId: product.id,
                id: firstVariant.id,
                title: customBundleTitle || product.title,
                description: product.description,
                image: flavorImage,
                benefitsOtp: this.formatBenefits(metafields?.bundle_benefits_otp),
                benefitsSubscription: this.formatBenefits(metafields?.bundle_benefits),
                refillText: metafields?.bundle_refill_text,
                link: { handle: product.handle, url: `/products/${product.handle}` },

                prices: {
                  otp: {
                    currentPrice: firstVariant.price,
                    compareAtPrice: oneTimeReferencePrice > variantPrice ? oneTimeReferencePrice : null,
                  },
                  subscription: {
                    currentPrice: subscriptionPrice,
                    oldPrice: subscriptionOldPrice,
                  },
                },

                savings: {
                  otp: oneTimeReferencePrice - effectiveOtpPrice,
                  subscription: oneTimeReferencePrice - effectiveSubscriptionPrice,
                },

                // IMPORTANT: homepage badge should be full string e.g. "Save 38%"
                badge_text: {
                  otp: this.buildBadgeText(otpPct),
                  subscription: this.buildBadgeText(subscriptionPct),
                },

                selling_plan: hasSellingPlan ? firstVariant.selling_plan_allocations[0] : null,
                promotionalPrice: promotionalPriceValue,
                subscriptionPromotionalPrice: subscriptionPromotionalPriceValue,
                subscriptionPromoLabel: subscriptionPromoLabel || null,
              });
            }
          );

          this.currentFlavor = this.flavors[0] || null;
        },

        updateForm() {
          if (!this.bundleForm || !this.currentFlavor) return;

          const parentIdField = this.bundleForm.querySelector('input[name="product-id"]');
          const variantIdField = this.bundleForm.querySelector('input[name="id"]');
          const sellingPlanField = this.bundleForm.querySelector('input[name="selling_plan"]');

          if (parentIdField) parentIdField.value = this.currentFlavor.parentId;
          if (variantIdField) variantIdField.value = this.currentFlavor.id;

          // Only set selling plan if we actually have one AND we're in subscription mode.
          const planId = this.currentFlavor?.selling_plan?.selling_plan_id;
          if (sellingPlanField) sellingPlanField.value = (this.isSubscription && planId) ? planId : "";
        },

        changeSelectedFlavor(flavorId) {
          this.currentFlavor = this.flavors.find((flavor) => flavor.id == flavorId) || this.currentFlavor;
          this.updateForm();
        },

        changeSelectedPurchaseType(purchaseTypeId) {
          this.purchaseType = this.purchaseTypes.find((type) => type.id === purchaseTypeId) || { id: purchaseTypeId };
          this.isSubscription = this.purchaseType?.id === "subscription";
          this.updateForm();
        },

        formatPrice(price) {
          if (!price) return this.pricePattern.replace("[amount]", "0.00");
          return this.pricePattern.replace("[amount]", (price / this.currencyDivider).toFixed(2));
        },
      };
    }
  );
});
